import axios from 'axios';
import { refreshAccessToken } from './tokenRefresh';
import { redirectToLogin } from '@/utils/navigateTo';

// Get API URL from electron if available, otherwise use relative path for proxy
const getApiUrl = async (): Promise<string> => {
  // Check if running in Electron
  if (window.electronAPI?.isElectron) {
    const serverUrl = await window.electronAPI.getServerUrl();
    if (serverUrl) {
      return serverUrl;
    }
  }
  // For web browser, use empty string to make URLs relative (Vite proxy will handle it)
  return '';
};

let API_URL = '';

// Update API_URL if in Electron
if (window.electronAPI?.isElectron) {
  getApiUrl().then((url) => {
    API_URL = url;
    api.defaults.baseURL = `${API_URL}/api`;
  });
}

const api = axios.create({
  baseURL: API_URL ? `${API_URL}/api` : '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Normalize paths to avoid double "/api" when baseURL already includes it
const normalizeApiPath = (url?: string): string | undefined => {
  if (!url) return url;
  // Leave absolute URLs untouched
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  // Strip leading /api/ or /api to prevent duplication with baseURL
  if (url === '/api') return '';
  if (url.startsWith('/api/')) return url.substring(5);
  return url;
};

// Request interceptor to normalize URL and add auth token
api.interceptors.request.use(
  (config) => {
    // Normalize config.url for accidental /api prefixes
    if (typeof config.url === 'string') {
      config.url = normalizeApiPath(config.url);
    }

    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle token refresh and permission errors
api.interceptors.response.use(
  (response) => {
    // Automatically unwrap unified response envelope
    if (response.data && response.data.success === true && 'data' in response.data) {
      return { ...response, data: response.data.data };
    }
    return response;
  },
  async (error) => {
    // Handle 403 permission errors with toast notifications
    if (error.response?.status === 403) {
      const errorMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.error ||
        'You do not have permission to perform this action';
      // Dispatch custom event for toast notification
      window.dispatchEvent(
        new CustomEvent('permission-denied', {
          detail: { message: errorMessage },
        }),
      );
    }

    const originalRequest = error.config;

    // Attempt silent token refresh on 401 (don't redirect immediately)
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshed = await refreshAccessToken();

        if (refreshed) {
          // Token refreshed successfully, retry original request
          const newToken = localStorage.getItem('accessToken');
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api(originalRequest);
          }
        }
      } catch (refreshError) {
        console.error('[api] Token refresh failed:', refreshError);
      }

      // If refresh failed, redirect to login
      redirectToLogin();
      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);

export default api;
